import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { useManagementAuth } from '../../auth/ManagementAuthContext';
import api from '../../services/api';
import { CompanyDetail } from '../../types';

const CompanyStoreListPage: React.FC = () => {
  const navigate = useNavigate();
  const { managementAccount, managementToken } = useManagementAuth();
  const [companies, setCompanies] = useState<CompanyDetail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!managementToken) {
        setLoading(false);
        return;
      }
      try {
        const data = await api.managementCompany.getAll(managementToken);
        setCompanies(data);
      } catch (error) {
        console.error('企業一覧の取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, [managementToken]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {managementAccount?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            企業・店舗一覧
          </Typography>
        </Box>
      </Box>

      {companies.map((company) => (
        <Card key={company.id} sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                {company.name}
              </Typography>
              <Chip label={`${company.stores.length}店舗`} size="small" color="primary" variant="outlined" />
            </Box>
            {company.address && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {company.address}
              </Typography>
            )}
          </CardContent>

          {/* 企業管理へのリンク */}
          <CardActionArea
            onClick={() => navigate(`/management/company/${company.id}`)}
            sx={{ px: 2, py: 1.5 }}
          >
            <Typography variant="body2" color="primary" fontWeight="bold">
              企業管理画面を開く →
            </Typography>
          </CardActionArea>

          <Divider />

          {/* 店舗一覧 */}
          <List disablePadding>
            {company.stores.map((store) => (
              <ListItem key={store.id} disablePadding>
                <ListItemButton onClick={() => navigate(`/management/company/${company.id}/store/${store.id}`)}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <StoreIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={store.name || '（店舗名未設定）'}
                    secondary={store.address || ''}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {company.stores.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="登録店舗がありません"
                  sx={{ color: 'text.secondary', textAlign: 'center' }}
                />
              </ListItem>
            )}
          </List>
        </Card>
      ))}

      {companies.length === 0 && (
        <Card>
          <CardContent>
            <Typography color="text.secondary" textAlign="center">
              管理対象の企業がありません
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CompanyStoreListPage;
